import IosShareIcon from '@mui/icons-material/IosShare';
import { Box, SwipeableDrawer, Theme, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useState } from 'react';
import { ContentTypeRichTextContent } from '@/types/generated';
import BlogAuthorDetails from '../BlogAuthorDetails';
import BlogDetailsImageAssetContainer from '../BlogDetailsImageAssetContainer';
import { IBlogDetailsPage } from '../BlogDetailsPage';
import { BLOG_AUTHOR_LABELS } from '../BlogDetailsPage.constants';
import { getContentContainerStyles, getImageWrapperStyles, getContainerStyles, shareContentStyles, socialMediaLinkStyles, drawerStyles, pullerBoxStyles, mobileIconContainer, mobileTitleStyles, shareIconStyles, desktopOnly, mobileOnly } from '../BlogDetailsPage.styles';
import SocialMediaShareLinks from '../SocialMediaShareLinks';
import ContentTypeRichText from '@shared-components/components/ContentTypeRichText';
import PullerTab from '@shared-components/components/PullerTab/PullerTab';
import TAGButton from '@shared-components/components/TAGButton';
import { resolveTypographyVariantStyles } from '@utils/resolveTypographyVariantStyles';
interface IBlogDetailsPageStandardProps extends IBlogDetailsPage {
  theme: Theme;
  isSmallScreen: boolean;
}
export default function BlogDetailsPageStandard(props: Readonly<IBlogDetailsPageStandardProps>) {
  const {
    title,
    date,
    summary,
    text,
    author,
    reviewer,
    imageAsset,
    blogImage,
    theme,
    isSmallScreen
  } = props;
  const [openDrawer, setOpenDrawer] = useState(false);
  const formattedDate = format(new Date(date), 'MM.dd.yyyy');
  const responsiveTypeVariantSX = [resolveTypographyVariantStyles(theme, 'bodyMediumBook'), {
    [theme.breakpoints.up('md')]: {
      ...resolveTypographyVariantStyles(theme, 'bodyLargeBook')
    }
  }];
  return <Box data-testid="blog_detail_page_container" sx={getContainerStyles} data-sentry-element="Box" data-sentry-component="BlogDetailsPageStandard" data-sentry-source-file="BlogDetailsPageStandard.tsx">
			<Box sx={[getImageWrapperStyles, desktopOnly]} data-sentry-element="Box" data-sentry-source-file="BlogDetailsPageStandard.tsx">
				<BlogDetailsImageAssetContainer blogImage={blogImage} imageAsset={imageAsset} usePreloader={true} isSmallScreen data-sentry-element="BlogDetailsImageAssetContainer" data-sentry-source-file="BlogDetailsPageStandard.tsx" />
			</Box>
			<Box data-testid="blog_detail_page_content_container" sx={[getContentContainerStyles]} component="article" data-sentry-element="Box" data-sentry-source-file="BlogDetailsPageStandard.tsx">
				<Typography data-testid="blog_detail_page_content_date" color="text.secondary" component="span" sx={responsiveTypeVariantSX} data-sentry-element="Typography" data-sentry-source-file="BlogDetailsPageStandard.tsx">
					{formattedDate}
				</Typography>

				<Typography color="text.primary" component="h1" textAlign={{
        xs: 'center',
        md: 'left'
      }} sx={[resolveTypographyVariantStyles(theme, 'heroMedium'), {
        [theme.breakpoints.up('md')]: {
          ...resolveTypographyVariantStyles(theme, 'heroLarge')
        }
      }]} data-sentry-element="Typography" data-sentry-source-file="BlogDetailsPageStandard.tsx">
					{title}
				</Typography>
				{summary?.json && <ContentTypeRichText content={summary as ContentTypeRichTextContent} dataTestId="blog_detail_page_content_summary_richtext" contentMaxWidth="100%" disableGutters={true} disablePaddingOn={['Top', 'Bottom']} backgroundColor="transparent" centerAlign={!!isSmallScreen} docProps={{
        color: 'text.secondary',
        sx: responsiveTypeVariantSX
      }} containerSx={{
        marginBottom: '0.75rem'
      }} />}
				<Box sx={[getImageWrapperStyles, mobileOnly]} data-sentry-element="Box" data-sentry-source-file="BlogDetailsPageStandard.tsx">
					<BlogDetailsImageAssetContainer blogImage={blogImage} imageAsset={imageAsset} usePreloader={false} isSmallScreen data-sentry-element="BlogDetailsImageAssetContainer" data-sentry-source-file="BlogDetailsPageStandard.tsx" />
				</Box>
				<ContentTypeRichText content={text as ContentTypeRichTextContent} dataTestId="blog_detail_page_content_text_richtext" contentMaxWidth="100%" disableGutters={true} disablePaddingOn={['Top', 'Bottom']} containerSx={{
        marginTop: '0.75rem'
      }} backgroundColor="transparent" data-sentry-element="ContentTypeRichText" data-sentry-source-file="BlogDetailsPageStandard.tsx" />
				{author && <BlogAuthorDetails {...author} sectionLabel={BLOG_AUTHOR_LABELS.WRITTEN_BY} />}
				{reviewer && <BlogAuthorDetails {...reviewer} sectionLabel={BLOG_AUTHOR_LABELS.MEDICALLY_REVIEWED_BY} />}
				<Box sx={shareContentStyles} data-sentry-element="Box" data-sentry-source-file="BlogDetailsPageStandard.tsx">
					<Typography data-testid="share_article_title_text" variant={'header3'} color="text.primary" component="span" data-sentry-element="Typography" data-sentry-source-file="BlogDetailsPageStandard.tsx">
						{isSmallScreen ? 'Share this article' : 'Share'}
					</Typography>
					<Box sx={socialMediaLinkStyles()} data-sentry-element="Box" data-sentry-source-file="BlogDetailsPageStandard.tsx">
						{isSmallScreen ? <TAGButton size="S" variant="secondaryDefault" endIcon={<IosShareIcon sx={shareIconStyles} />} onClick={() => setOpenDrawer(true)} dataTestId="mobile_shareable_link_button">
								Share
							</TAGButton> : <SocialMediaShareLinks isCompactDesign={false} />}
					</Box>
				</Box>
			</Box>
			{isSmallScreen && <SwipeableDrawer anchor="bottom" onClose={() => setOpenDrawer(false)} onOpen={() => setOpenDrawer(true)} open={openDrawer} sx={{
      ...drawerStyles,
      ...mobileOnly(theme)
    }} data-testid={'shareable_mobile_link_drawer'}>
					<Box sx={pullerBoxStyles}>
						<PullerTab />
					</Box>
					<Typography sx={mobileTitleStyles} variant={'header2'} color="text.primary" component="span">
						Share this article
					</Typography>
					<Box sx={mobileIconContainer}>
						<SocialMediaShareLinks />
					</Box>
				</SwipeableDrawer>}
		</Box>;
}